<template>
  <content-subpage
    class="media-access-restriction-content-subpage"
    :visible="visible"
    :titles="titles"
    @content-subpage-hidden="actionHidden"
  >
    <template #main-content>
      <media-component
        ref="media"
        :uuid="uuid"
        type="mo"
        :mediatype="mediaType"
        :show-ai-info-icon="false"
        :cue-points="cuePoints"
        :action-tools="false"
        :zoom-tools="false"
        is-inline
      />
    </template>
    <template #sub-content>
      <overlay-loader v-if="isLoading" />
      <template v-else>
        <h5 class="title">{{ t("media_access_restriction_title") }}</h5>
        <div v-if="validityLabel" class="validity">{{ validityLabel }}</div>
        <cv-form class="meta-data">
          <!-- Subject -->
          <dl v-if="isRestricted" class="field-set type-text subject">
            <dt class="field-label">{{ t("media_access_restriction_subject_label") }}</dt>
            <dd class="field-value">
              <span class="message">{{ subjectList }}</span>
            </dd>
          </dl>
          <!-- Level -->
          <dl v-if="isRestricted" class="field-set type-text level">
            <dt class="field-label">{{ t("media_access_restriction_level_label") }}</dt>
            <dd class="field-value">
              <span class="message">{{ levelList }}</span>
            </dd>
          </dl>
        </cv-form>
      </template>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import i18n from "@plugins/i18n.ts";
import {
  MEDIA_TYPE,
  MEDIA_FILE_STATUS,
  MO_FETCH_INTERVAL,
  MO_DEFAULT_FETCH_INTERVAL,
  AVAILABILITY,
  MediaType,
} from "../media.constant";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useErrorHandler } from "@/plugins/common/composables/error.ts";
import mediaObjectApi from "@apis/media.object.api";
import DateTimeFormatter from "@utils/datetime.formatter";
import { isEmpty } from "@utils/var.check";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";
import { CuePoints, MediaMetadata } from "@plugins/media/types.ts";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";

interface Params {
  uuid: string;
  mediaType: MediaType | string;
  cuePoints?: CuePoints;
}

const props = defineProps<{ params: Params }>();

const { t } = useI18n();
const { titles, visible, actionHidden, defaultExpose } = useContentSubpage(props.params);
const { passiveApiFailHandler } = useErrorHandler();

const media = ref<HTMLElement | undefined>(undefined);

const isLoading = ref(true);
const uuid = ref(props.params.uuid);
const mediaType = ref(props.params.mediaType);
const cuePoints = ref(props.params.cuePoints);

const mediaMetadata = ref<MediaMetadata>({});
const fileStatus = ref<MediaMetadata["fileStatus"]>(undefined);
computed<boolean>(() => {
  return (
    mediaType.value === MEDIA_TYPE.VIDEO ||
    mediaType.value === MEDIA_TYPE.AUDIO ||
    mediaType.value === MEDIA_TYPE.IMAGE ||
    mediaType.value === MEDIA_TYPE.HTML5
  );
});
const isMediaFileInProcessing = computed<boolean>(() => {
  return (
    fileStatus.value === MEDIA_FILE_STATUS.UPLOADING ||
    fileStatus.value === MEDIA_FILE_STATUS.SCANNING ||
    fileStatus.value === MEDIA_FILE_STATUS.PROCESSING
  );
});

const validityLabel = computed<string | null>(() => {
  if (isEmpty(mediaMetadata.value.endDate)) {
    return null;
  }
  const date = DateTimeFormatter.formatLocalDateTimeToDateOnly(mediaMetadata.value.endDate);
  return String(i18n.t("media_access_restriction_validity", { date }));
});

const isRestricted = computed<boolean>(() => {
  return mediaMetadata.value.availability === AVAILABILITY.RESTRICTED.value;
});

const subjectList = computed<string>(() => {
  const list = mediaMetadata.value.subjects?.map((item) => item.subjectName ?? "").join(", ") ?? "";
  return isEmpty(list) ? "-" : list;
});

const levelList = computed<string>(() => {
  const list =
    mediaMetadata.value.levelAndStreams?.map((item) => item.displayName ?? "").join(", ") ?? "";
  return isEmpty(list) ? "-" : list;
});

function getMetadata(retryNo: number): void {
  mediaObjectApi.getMetaData({ uuid: uuid.value }).then(
    (res) => {
      if (res.code === "ok") {
        mediaMetadata.value = res.data;
        fileStatus.value = res.data.fileStatus;
        if (isMediaFileInProcessing.value) {
          let interval = MO_DEFAULT_FETCH_INTERVAL;
          if (retryNo < MO_FETCH_INTERVAL.length) {
            interval = MO_FETCH_INTERVAL[retryNo];
          }
          setTimeout(() => {
            getMetadata(retryNo + 1);
          }, interval);
        } else {
          isLoading.value = false;
        }
      } else {
        passiveApiFailHandler(res.errorMap);
      }
    },
    (err) => passiveApiFailHandler(err),
  );
}

onMounted(() => {
  titles.value.push(String(t("media_access_restriction_title")));
  getMetadata(0);
});

defineExpose({
  ...defaultExpose,
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/spacing";

.content-subpage.media-access-restriction-content-subpage {
  .sub {
    > .title {
      margin-bottom: $spacing-05;
    }
  }
}
</style>
